import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { getProviderList } from "../../api/providers";
import { getCurrencyList } from "../../api/common/currencies";
import { createScheduledPayment } from "../../api/scheduledPayments";
import { useSnack } from "../../services/SnakbarProvider";

const emptyItem = () => ({ description: "", amount: "" });

export default function ScheduledPaymentForm({ companyId, onSubmit }) {
  const { openSnack } = useSnack();

  const [provider, setProvider] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);
  const [items, setItems] = useState([emptyItem()]);
  const [loading, setLoading] = useState(false);

  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleAddItem = () => setItems((prev) => [...prev, emptyItem()]);

  const handleRemoveItem = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  const isValid = () =>
    provider &&
    currency &&
    paymentDate &&
    items.length > 0 &&
    items.every((item) => item.amount !== "" && !isNaN(Number(item.amount)));

  const handleSubmit = async () => {
    if (!isValid() || loading) return;

    try {
      setLoading(true);
      await createScheduledPayment({
        scheduledPayment: {
          companyId,
          providerId: provider.id,
          currencyId: currency.id,
          paymentDate: paymentDate.format("YYYY-MM-DD"),
        },
        items: items.map((item) => ({
          description: item.description || null,
          amount: Number(item.amount),
          raPaymentId: null,
        })),
      });
      openSnack("Programación de pago creada exitosamente", "success");
      if (onSubmit) onSubmit();
    } catch (error) {
      openSnack(
        error.response?.data?.Details || error.message || "Error al crear la programación de pago",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={1}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LGAutocomplete
            fetchFunction={getProviderList}
            queryParams={{ companyId }}
            optionLabel="code"
            value={provider}
            onChange={(event, value) => setProvider(value)}
            label="Proveedor"
          />
        </Grid>
        <Grid item xs={3}>
          <LGAutocomplete
            fetchFunction={getCurrencyList}
            optionLabel="code"
            value={currency}
            onChange={(event, value) => setCurrency(value)}
            label="Moneda"
          />
        </Grid>
        <Grid item xs={3}>
          <MyDatePicker
            fullWidth
            slotProps={{ textField: { fullWidth: true } }}
            label="Fecha de pago"
            value={paymentDate}
            onChange={(value) => setPaymentDate(value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6">Renglones</Typography>
        </Grid>

        {items.map((item, index) => (
          <React.Fragment key={index}>
            <Grid item xs={7}>
              <TextField
                fullWidth
                label="Descripción"
                value={item.description}
                onChange={(e) => handleItemChange(index, "description", e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label="Monto"
                value={item.amount}
                onChange={(e) => handleItemChange(index, "amount", e.target.value)}
              />
            </Grid>
            <Grid item xs={1} display="flex" alignItems="center">
              <IconButton
                onClick={() => handleRemoveItem(index)}
                disabled={items.length === 1}
                color="error"
              >
                <Icon>delete</Icon>
              </IconButton>
            </Grid>
          </React.Fragment>
        ))}

        <Grid item xs={12}>
          <Button startIcon={<Icon>add</Icon>} onClick={handleAddItem}>
            Agregar renglón
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || !isValid()}
            >
              {loading ? "Guardando..." : "Crear programación de pago"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
